import {getStore} from 'src/v2/state/store';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {getWalletKey} from 'src/v2/util/getWalletKey';
import {NetworkType} from 'src/v2/state/types';
import {ExtensionWalletProvider} from 'src/types';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {
  handleError,
  clearErrorBanner,
  QR_CODE_EXPIRATION_ERROR,
} from 'src/v2/components/error/handleError';
import {useIntl} from '@sail/intl';
import {isMobile} from 'src/utils/utils';
import {getLogger} from 'src/utils/logger';

type StartWalletConnectionParams = (
  | {
      provider: ExtensionWalletProvider;
    }
  | {
      provider: 'WalletConnect';
      walletKey: string;
    }
) & {
  networkType: NetworkType;
};

export type StartWalletConnection = (
  params: StartWalletConnectionParams,
) => Promise<void>;

export const useIsWalletConnecting = (): {
  startWalletConnection: StartWalletConnection;
  endWalletConnection: () => void;
  isWalletConnecting: boolean;
} => {
  const intl = useIntl();
  const store = getStore();
  const [updateSessionOnWalletConnect] = useUpdateSessionOnWalletConnect();
  const walletClient = useWalletConnectionClient();
  const {mode} = useSessionContext();

  const isWalletConnecting = store.useSelector(
    (state) => state.overlay === 'CONNECT_WALLET',
  );

  const startWalletConnection = async (params: StartWalletConnectionParams) => {
    clearErrorBanner();

    // When using WalletConnect, show QR code instead of overlay
    if (params.provider !== 'WalletConnect' || isMobile()) {
      store.setValue((state) => {
        state.overlay = 'CONNECT_WALLET';
      });
    }

    try {
      // We will map the key to `walletconnect` in the inner iframe.
      // This is a hack so we can render the selected wallet properties instead of the generic WC properties.
      const key =
        params.provider === 'WalletConnect'
          ? params.walletKey
          : getWalletKey(params.provider, params.networkType);

      const wallet = await walletClient.connectWallet(key);

      await updateSessionOnWalletConnect({
        walletAddress: wallet.address,
        network: wallet.network,
        type: wallet.type,
        walletKey: wallet.key,
      });

      store.setValue((state) => {
        state.screen = mode === 'setup' ? 'SETUP' : 'PAY';
        state.overlay = null;
      });
    } catch (error) {
      // Filter out "proposal expired" errors for WalletConnect since auto-refresh handles them
      if (
        params.provider === 'WalletConnect' &&
        error instanceof Error &&
        error.message.toLowerCase().includes(QR_CODE_EXPIRATION_ERROR)
      ) {
        // Log QR code expiration for analytics
        const logger = getLogger();
        logger.log('crypto_payin_session.error', {
          error_code: 'qr_code_expired',
        });

        // Silently ignore proposal expired errors for WalletConnect - auto-refresh handles this
        return;
      }
      handleError({error, intl, context: 'wallet_connection'});
    }
  };

  const endWalletConnection = () => {
    store.setValue((state) => {
      state.overlay = null;
    });
  };

  return {
    startWalletConnection,
    endWalletConnection,
    isWalletConnecting,
  };
};
